import {
  CHALLENGE_BAND_INFO,
  bandForExpectedScore,
  type ChallengeSummary,
} from '@foosball/shared';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { ApiError } from '../api/client';
import { Button, Card, FormError } from '../components/ui';
import {
  useAnswerChallenge,
  useCancelChallenge,
  useChallenges,
} from '../hooks/useChallenges';

/** The other player, whichever side of the challenge the viewer is on. */
function otherPlayer(challenge: ChallengeSummary) {
  return challenge.direction === 'sent' ? challenge.opponent : challenge.challenger;
}

function Odds({
  challenge,
  viewerIsChallenger,
}: {
  challenge: ChallengeSummary;
  viewerIsChallenger: boolean;
}) {
  if (challenge.band === null || challenge.expectedScore === null) return null;

  // The band is stored from the challenger's point of view, so the recipient
  // sees its mirror image — their "easy game" is your uphill one. Derived by
  // flipping the win probability and re-banding, never by restating the
  // thresholds here: two copies of those would drift the first time one moves.
  const label = viewerIsChallenger
    ? CHALLENGE_BAND_INFO[challenge.band].label
    : CHALLENGE_BAND_INFO[bandForExpectedScore(1 - challenge.expectedScore)].label;

  return (
    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
      {label}
    </span>
  );
}

function ChallengeCard({
  challenge,
  children,
}: {
  challenge: ChallengeSummary;
  children?: React.ReactNode;
}) {
  const them = otherPlayer(challenge);

  return (
    <Card className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-semibold">
            <Link to={`/players/${them.id}`} className="hover:underline">
              {them.displayName}
            </Link>
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {challenge.format} · {new Date(challenge.createdAt).toLocaleDateString()}
          </p>
        </div>
        <Odds challenge={challenge} viewerIsChallenger={challenge.direction === 'sent'} />
      </div>

      {challenge.message && (
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm italic dark:bg-slate-800">
          “{challenge.message}”
        </p>
      )}

      {children}
    </Card>
  );
}

export function ChallengesPage() {
  const navigate = useNavigate();
  const challenges = useChallenges();
  const answer = useAnswerChallenge();
  const cancel = useCancelChallenge();
  const [error, setError] = useState<string | null>(null);

  async function run(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
    }
  }

  const data = challenges.data;
  const busy = answer.isPending || cancel.isPending;

  return (
    <>
      <div className="mb-5 flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold tracking-tight">Challenges</h1>
        <Link
          to="/challenges/new"
          className="shrink-0 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
        >
          Challenge someone
        </Link>
      </div>

      <FormError message={error} />

      {challenges.isPending && <p className="text-sm text-slate-400">Loading…</p>}

      {data && (
        <div className="space-y-6">
          <section>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Waiting for your answer
            </h2>
            {data.incoming.length === 0 ? (
              <p className="text-sm text-slate-400">Nothing right now.</p>
            ) : (
              <div className="space-y-3">
                {data.incoming.map((challenge) => (
                  <ChallengeCard key={challenge.id} challenge={challenge}>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        disabled={busy}
                        onClick={() =>
                          void run(() => answer.mutateAsync({ id: challenge.id, answer: 'accept' }))
                        }
                      >
                        Accept
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        disabled={busy}
                        onClick={() =>
                          void run(() => answer.mutateAsync({ id: challenge.id, answer: 'decline' }))
                        }
                      >
                        Decline
                      </Button>
                    </div>
                  </ChallengeCard>
                ))}
              </div>
            )}
          </section>

          <section>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Scheduled
            </h2>
            {data.scheduled.length === 0 ? (
              <p className="text-sm text-slate-400">No fixtures arranged.</p>
            ) : (
              <div className="space-y-3">
                {data.scheduled.map((challenge) => (
                  <ChallengeCard key={challenge.id} challenge={challenge}>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Agreed — it is up to the two of you when you play.
                    </p>
                    <Button
                      type="button"
                      onClick={() => void navigate(`/matches/new?challenge=${challenge.id}`)}
                    >
                      Enter the result
                    </Button>
                  </ChallengeCard>
                ))}
              </div>
            )}
          </section>

          <section className="pb-4">
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Sent, awaiting a reply
            </h2>
            {data.outgoing.length === 0 ? (
              <p className="text-sm text-slate-400">None outstanding.</p>
            ) : (
              <div className="space-y-3">
                {data.outgoing.map((challenge) => (
                  <ChallengeCard key={challenge.id} challenge={challenge}>
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Expires {new Date(challenge.expiresAt).toLocaleDateString()}
                      </p>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void run(() => cancel.mutateAsync(challenge.id))}
                        className="shrink-0 text-xs text-slate-500 underline hover:text-slate-800 disabled:opacity-50 dark:text-slate-400 dark:hover:text-slate-100"
                      >
                        Withdraw
                      </button>
                    </div>
                  </ChallengeCard>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

    </>
  );
}
